import { Avatar, type AvatarProps, useMantineTheme } from '@mantine/core';
import queryString from 'query-string';
import { forwardRef, useMemo } from 'react';

export const logoDevVendorDefaults: Pick<LogoDevParams, 'size' | 'greyscale'> =
  {
    size: 128,
    greyscale: false,
  };

export const logoDevKlueDefaults: LogoDevParams = {
  retina: true,
  format: 'png',
  fallback: '404',
};

export type LogoDevParams = {
  size?: number;
  greyscale?: boolean;
  retina?: boolean;
  format?: 'png' | 'jpg' | 'webp';
  fallback?: '404' | 'monogram';
};

export type EntityLogoProps = {
  name: string;
  url: string | null;
  logoProps?: LogoDevParams;
} & AvatarProps;

const EntityLogo = forwardRef<HTMLDivElement, EntityLogoProps>(
  ({ name, url, logoProps = logoDevVendorDefaults, ...rest }, ref) => {
    const { primaryColor } = useMantineTheme();
    const domain = url
      ? (() => {
          try {
            return new URL(url).hostname;
          } catch {
            return null;
          }
        })()
      : null;

    const params = useMemo(
      () =>
        queryString.stringify({
          ...logoDevVendorDefaults,
          ...logoDevKlueDefaults,
          ...logoProps,
        }),
      [logoProps],
    );

    // Query the logo endpoint on the monolith
    const klueLogoUrl = domain ? `/api/logo/${domain}?${params}` : null;

    return (
      <Avatar
        ref={ref}
        bd="1px var(--mantine-color-gray-4) solid"
        src={klueLogoUrl}
        name={name}
        size={38}
        radius="10%"
        color={`${primaryColor}.8`}
        alt={name}
        {...rest}
      />
    );
  },
);

EntityLogo.displayName = 'EntityLogo';

export default EntityLogo;
